import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, AlertCircle, Download, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================================
// SAMS SP26 ONLY
// Compares Production (receipts + WIP) with Sales (orderfile)
// Locked to Sams customer and SP26 season
// ============================================================================
export default function SamsProductionSalesDashboard() {
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [programData, setProgramData] = useState([]);
  const [labelData, setLabelData] = useState([]);
  const [countryOriginData, setCountryOriginData] = useState([]);

  // Locked filters - Sams SP26 only
  const samsCustomers = ['SAMS']; // mg2_customer for production
  const samsSalesCustomers = ["SAM'S CLUB DSV", "SAM'S"]; // Customer_Name for sales
  const samsLabels = ['SAMS', 'MEMBERS MARK', 'MM'];
  const selectedSeason = 'SP26';

  // Excluded labels (clicking a label excludes it)
  const [excludedLabels, setExcludedLabels] = useState([]);
  // Exclude All Year Styles filter
  const [excludeAllYear, setExcludeAllYear] = useState(true);

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '$0';
    const num = Number(value) || 0;
    if (Math.abs(num) >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(num) >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toFixed(0)}`;
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (value === null || value === undefined || value === 0) return '0';
    return Number(value).toLocaleString();
  };

  // Format percentage (GP%)
  const formatPct = (value) => {
    if (value === null || value === undefined) return '-';
    return `${(Number(value) * 100).toFixed(1)}%`;
  };

  // Calculate GP%
  const calcGP = (revenue, cost) => {
    if (!revenue || revenue === 0) return 0;
    return (revenue - cost) / revenue;
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const getProductionSalesData = httpsCallable(functions, 'getProductionSalesData', { timeout: 150000 });

      // Build filter params - locked to Sams SP26 Division 10, 60
      // When (No Label) is excluded, switch to include mode for precise filtering
      // This ensures non-standard labels don't slip through
      const noLabelExcluded = excludedLabels.includes('(No Label)');
      const hasExclusions = excludedLabels.length > 0;
      let filterLabels = null;
      let filterExcludeLabels = null;

      // No extra exclusions for Sams
      const alwaysExclude = [];

      if (hasExclusions) {
        if (noLabelExcluded) {
          // Include mode: only show items with specific non-excluded standard labels
          const includeLabels = samsLabels.filter(l => !excludedLabels.includes(l));
          if (includeLabels.length > 0) {
            filterLabels = includeLabels;
          }
          // If all labels excluded (including No Label), filterLabels stays null = show nothing
        } else {
          // Exclude mode: remove specific labels, keep everything else
          filterExcludeLabels = [...new Set([...excludedLabels, ...alwaysExclude])];
        }
      } else if (alwaysExclude.length > 0) {
        // No user exclusions, but still exclude alwaysExclude if any
        filterExcludeLabels = alwaysExclude;
      }

      const filterParams = {
        seasons: [selectedSeason],
        customers: samsCustomers,
        salesCustomers: samsSalesCustomers,
        divisions: ['10', '60'],
        labels: filterLabels,
        excludeLabels: filterExcludeLabels,
        excludeAllYear: excludeAllYear,
      };

      // Fetch data for Customer, Program, Label, and CountryOriginType groupings
      const [customerResult, programResult, labelResult, countryOriginResult] = await Promise.all([
        getProductionSalesData({ ...filterParams, groupBy: 'Customer' }),
        getProductionSalesData({ ...filterParams, groupBy: 'Program' }),
        getProductionSalesData({ ...filterParams, groupBy: 'Label' }),
        getProductionSalesData({ ...filterParams, groupBy: 'CountryOriginType' }),
      ]);

      setCustomerData(customerResult.data.summary || []);
      setProgramData(programResult.data.summary || []);
      setLabelData(labelResult.data.summary || []);
      setCountryOriginData(countryOriginResult.data.summary || []);

    } catch (err) {
      console.error('Error fetching Production vs Sales data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [excludedLabels, excludeAllYear]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle label exclusion (clicking excludes/includes)
  const toggleLabelExclusion = (label) => {
    setExcludedLabels(prev =>
      prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  // Clear filters
  const clearFilters = () => {
    setExcludedLabels([]);
    setExcludeAllYear(false);
  };

  const hasActiveFilters = excludedLabels.length > 0 || excludeAllYear;

  // Export to Excel
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    const mapRow = (row, groupField) => {
      const totalProdRev = Number(row.Total_Production_Revenue) || 0;
      const totalProdCost = Number(row.Total_Production_Cost) || 0;
      const invoicedRev = Number(row.Invoiced_Revenue) || 0;
      const invoicedCost = Number(row.Invoiced_Cost) || 0;
      const leftToShipRev = totalProdRev - invoicedRev;
      const leftToShipCost = totalProdCost - invoicedCost;
      const onPickRev = Number(row.On_Pick_Revenue) || 0;
      const onPickCost = Number(row.On_Pick_Cost) || 0;
      const receivedRev = Number(row.Received_Revenue) || 0;
      const receivedCost = Number(row.Received_Cost) || 0;
      const atsRev = receivedRev - invoicedRev - onPickRev;
      const atsCost = receivedCost - invoicedCost - onPickCost;

      return {
        [groupField]: row[groupField],
        'Total Prod $': totalProdRev,
        'Total Cost': totalProdCost,
        'Total Prod GP%': calcGP(totalProdRev, totalProdCost),
        'Invoice $': invoicedRev,
        'Invoice GP%': calcGP(invoicedRev, invoicedCost),
        'Left to Ship $': leftToShipRev,
        'LTS Cost': leftToShipCost,
        'LTS GP%': calcGP(leftToShipRev, leftToShipCost),
        'On Pick $': onPickRev,
        'On Pick GP%': calcGP(onPickRev, onPickCost),
        'ATS $': atsRev,
        'ATS Cost': atsCost,
        'ATS GP%': calcGP(Math.abs(atsRev), Math.abs(atsCost)),
      };
    };

    if (customerData.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(customerData.map(r => mapRow(r, 'Customer'))), 'By Customer');
    }
    if (programData.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(programData.map(r => mapRow(r, 'Program'))), 'By Program');
    }
    if (labelData.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(labelData.map(r => mapRow(r, 'Label'))), 'By Label');
    }

    XLSX.writeFile(wb, `Sams_ProductionVsSales_${selectedSeason}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();

    const pageHeight = doc.internal.pageSize.getHeight();

    // Sam's Club logo text
    doc.setFontSize(11);
    doc.setTextColor(0, 102, 179);
    doc.text("Sam's Club", 14, 15);

    // Title
    doc.setFontSize(16);
    doc.setTextColor(0, 102, 179); // Sam's blue
    doc.text(`Sam's Club Production vs Sales - ${selectedSeason} Div 10, 60`, 42, 15);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 42, 21);

    // Summary Cards Row
    const cardY = 31;
    const cardH = 18;
    const cardGap = 3;
    const cardW = (pageWidth - 28 - cardGap * 5) / 6;
    const cards = [
      { label: 'Total Production', value: formatCurrency(summary.totalProdRev), gp: formatPct(summary.totalProdGP), bg: [219, 234, 254] },
      { label: 'Invoice', value: formatCurrency(summary.invoicedRev), gp: formatPct(summary.invoicedGP), bg: [220, 252, 231] },
      { label: 'Left to Ship', value: formatCurrency(summary.totalProdRev - summary.invoicedRev), gp: formatPct(calcGP(summary.totalProdRev - summary.invoicedRev, summary.totalProdCost - summary.invoicedCost)), bg: [243, 232, 255] },
      { label: 'On Pick', value: formatCurrency(summary.onPickRev), gp: formatPct(summary.onPickGP), bg: [255, 237, 213] },
      { label: 'ATS', value: formatCurrency(summary.atsRev), gp: formatPct(summary.atsGP), bg: [204, 251, 241] },
      { label: 'Total WIP', value: formatCurrency(summary.inTransitRev + summary.wipNotTransitRev), gp: formatPct(calcGP(summary.inTransitRev + summary.wipNotTransitRev, summary.inTransitCost + summary.wipNotTransitCost)), bg: [255, 251, 235] },
    ];

    cards.forEach((card, i) => {
      const x = 14 + i * (cardW + cardGap);
      doc.setFillColor(...card.bg);
      doc.roundedRect(x, cardY, cardW, cardH, 2, 2, 'F');
      doc.setFontSize(7);
      doc.setTextColor(80, 80, 80);
      doc.text(card.label, x + cardW / 2, cardY + 5, { align: 'center' });
      doc.setFontSize(12);
      doc.setTextColor(30, 30, 30);
      doc.text(card.value, x + cardW / 2, cardY + 11, { align: 'center' });
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text(`${card.gp} GP`, x + cardW / 2, cardY + 15, { align: 'center' });
    });

    // Program Table
    const tableStartY = cardY + cardH + 8;
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text('By Program', 14, tableStartY);

    const headers = [['Program', 'Total Cost', 'Total Prod $', 'GP%', 'Invoice $', 'GP%', 'LTS Cost', 'Left to Ship $', 'GP%', 'On Pick $', 'GP%', 'ATS Cost', 'ATS $', 'GP%']];

    const mapPdfRow = (row) => {
      const totalProdRev = Number(row.Total_Production_Revenue) || 0;
      const totalProdCost = Number(row.Total_Production_Cost) || 0;
      const invoicedRev = Number(row.Invoiced_Revenue) || 0;
      const invoicedCost = Number(row.Invoiced_Cost) || 0;
      const leftToShipRev = totalProdRev - invoicedRev;
      const leftToShipCost = totalProdCost - invoicedCost;
      const onPickRev = Number(row.On_Pick_Revenue) || 0;
      const onPickCost = Number(row.On_Pick_Cost) || 0;
      const receivedRev = Number(row.Received_Revenue) || 0;
      const receivedCost = Number(row.Received_Cost) || 0;
      const atsRev = receivedRev - invoicedRev - onPickRev;
      const atsCost = receivedCost - invoicedCost - onPickCost;
      return [
        row.Program || '-',
        formatCurrency(totalProdCost), formatCurrency(totalProdRev), formatPct(calcGP(totalProdRev, totalProdCost)),
        formatCurrency(invoicedRev), formatPct(calcGP(invoicedRev, invoicedCost)),
        formatCurrency(leftToShipCost), formatCurrency(leftToShipRev), formatPct(calcGP(leftToShipRev, leftToShipCost)),
        formatCurrency(onPickRev), formatPct(calcGP(onPickRev, onPickCost)),
        formatCurrency(atsCost), formatCurrency(atsRev), formatPct(calcGP(Math.abs(atsRev), Math.abs(atsCost))),
      ];
    };

    const tableMargin = 14;
    const tableY = tableStartY + 4;

    // Calculate totals for footer row
    const totals = programData.reduce((acc, row) => {
      const tp = Number(row.Total_Production_Revenue) || 0;
      const tc = Number(row.Total_Production_Cost) || 0;
      const ir = Number(row.Invoiced_Revenue) || 0;
      const ic = Number(row.Invoiced_Cost) || 0;
      const opr = Number(row.On_Pick_Revenue) || 0;
      const opc = Number(row.On_Pick_Cost) || 0;
      const rcvr = Number(row.Received_Revenue) || 0;
      const rcvc = Number(row.Received_Cost) || 0;
      return {
        tpRev: acc.tpRev + tp, tpCost: acc.tpCost + tc,
        invRev: acc.invRev + ir, invCost: acc.invCost + ic,
        ltsRev: acc.ltsRev + (tp - ir), ltsCost: acc.ltsCost + (tc - ic),
        opRev: acc.opRev + opr, opCost: acc.opCost + opc,
        rcvRev: acc.rcvRev + rcvr, rcvCost: acc.rcvCost + rcvc,
      };
    }, { tpRev: 0, tpCost: 0, invRev: 0, invCost: 0, ltsRev: 0, ltsCost: 0, opRev: 0, opCost: 0, rcvRev: 0, rcvCost: 0 });

    const totalRow = [
      'TOTAL',
      formatCurrency(totals.tpCost), formatCurrency(totals.tpRev), formatPct(calcGP(totals.tpRev, totals.tpCost)),
      formatCurrency(totals.invRev), formatPct(calcGP(totals.invRev, totals.invCost)),
      formatCurrency(totals.ltsCost), formatCurrency(totals.ltsRev), formatPct(calcGP(totals.ltsRev, totals.ltsCost)),
      formatCurrency(totals.opRev), formatPct(calcGP(totals.opRev, totals.opCost)),
      formatCurrency(totals.rcvCost - totals.invCost - totals.opCost), formatCurrency(totals.rcvRev - totals.invRev - totals.opRev), formatPct(calcGP(Math.abs(totals.rcvRev - totals.invRev - totals.opRev), Math.abs(totals.rcvCost - totals.invCost - totals.opCost))),
    ];

    autoTable(doc, {
      startY: tableY,
      margin: { left: tableMargin, right: tableMargin },
      head: headers,
      body: [...programData.map(mapPdfRow), totalRow],
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2, lineColor: [147, 171, 219], lineWidth: 0.3 },
      headStyles: { fillColor: [0, 102, 179], textColor: 255, fontSize: 7, lineColor: [0, 102, 179] },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { halign: 'right' }, 2: { halign: 'right' },
        3: { halign: 'right' }, 4: { halign: 'right' },
        5: { halign: 'right' }, 6: { halign: 'right' },
        7: { halign: 'right' }, 8: { halign: 'right' },
        9: { halign: 'right' }, 10: { halign: 'right' },
        11: { halign: 'right' }, 12: { halign: 'right' },
        13: { halign: 'right' },
      },
      didParseCell: (data) => {
        // Bold the last row (totals)
        if (data.row.index === programData.length && data.section === 'body') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [226, 232, 240];
        }
      },
    });

    // In Transit by Country of Origin Chart
    const finalY = doc.lastAutoTable.finalY;
    let chartY = finalY + 10;
    if (countryOriginData.length > 0) {
      const coByCountry = {};
      countryOriginData.forEach(r => {
        const country = r.CountryOrigin || 'Unknown';
        const type = r.Prod_Type || 'Direct';
        const val = Number(r.In_Transit_Cost) || 0;
        if (val <= 0) return;
        if (!coByCountry[country]) coByCountry[country] = { total: 0, types: {} };
        coByCountry[country].total += val;
        coByCountry[country].types[type] = (coByCountry[country].types[type] || 0) + val;
      });
      const coCountries = Object.entries(coByCountry).sort((a, b) => b[1].total - a[1].total);
      const coMaxVal = coCountries.length > 0 ? coCountries[0][1].total : 1;
      const coTotal = coCountries.reduce((sum, [, d]) => sum + d.total, 0);
      const coTypeColors = { FOB: [20, 184, 166], DDP: [245, 158, 11], Direct: [99, 102, 241] };

      if (coCountries.length > 0) {
        const neededHeight = 14 + coCountries.length * 7 + 4;
        if (chartY + neededHeight > pageHeight - 15) {
          doc.addPage();
          chartY = 14;
        }
        const chartStartY = chartY - 3;

        doc.setFontSize(10);
        doc.setTextColor(30, 30, 30);
        doc.text('In Transit by Country of Origin (Cost)', tableMargin + 2, chartY);
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text(`Total: ${formatCurrency(coTotal)}`, pageWidth - tableMargin - 2, chartY, { align: 'right' });

        chartY += 5;
        let lx = tableMargin + 2;
        Object.entries(coTypeColors).forEach(([type, color]) => {
          doc.setFillColor(...color);
          doc.rect(lx, chartY - 2.5, 4, 3, 'F');
          doc.setFontSize(7);
          doc.setTextColor(100, 100, 100);
          doc.text(type, lx + 5, chartY);
          lx += doc.getTextWidth(type) + 12;
        });

        chartY += 5;
        const barMaxWidth = pageWidth - 28 - 25 - 35;
        const barH = 4.5;
        const barGap = 2;

        coCountries.forEach(([country, data]) => {
          doc.setFontSize(8);
          doc.setTextColor(60, 60, 60);
          doc.text(country, tableMargin + 2, chartY + 3);

          const barX = tableMargin + 22;
          doc.setFillColor(241, 245, 249);
          doc.roundedRect(barX, chartY, barMaxWidth, barH, 1, 1, 'F');

          const barWidth = Math.max((data.total / coMaxVal) * barMaxWidth, 2);
          let segX = barX;
          ['FOB', 'DDP', 'Direct'].forEach(type => {
            const typeVal = data.types[type] || 0;
            if (typeVal <= 0) return;
            const segWidth = (typeVal / data.total) * barWidth;
            doc.setFillColor(...coTypeColors[type]);
            doc.rect(segX, chartY, Math.max(segWidth, 0.5), barH, 'F');
            segX += segWidth;
          });

          const valX = barX + barMaxWidth + 3;
          doc.setFontSize(7);
          doc.setTextColor(50, 50, 50);
          doc.text(formatCurrency(data.total), valX, chartY + 3);
          const pct = coTotal > 0 ? ((data.total / coTotal) * 100).toFixed(1) : '0';
          doc.setTextColor(150, 150, 150);
          doc.text(`${pct}%`, valX + 22, chartY + 3);

          chartY += barH + barGap;
        });

        // Border around chart section
        doc.setDrawColor(147, 171, 219);
        doc.setLineWidth(0.5);
        doc.roundedRect(tableMargin - 1, chartStartY, pageWidth - tableMargin * 2 + 2, chartY - chartStartY + 2, 3, 3, 'S');
      }
    }

    // Footer: "This report is still in development mode" at bottom right
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    doc.text('This report is still in development mode', pageWidth - 14, pageHeight - 8, { align: 'right' });

    doc.save(`Sams_ProductionVsSales_${selectedSeason}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Data Table Component - Shows $ and GP% only
  const DataTable = ({ title, data, groupByField, color = 'slate' }) => {
    // Calculate totals for footer
    const calcTotals = data.reduce((acc, row) => {
      const totalProdRev = Number(row.Total_Production_Revenue) || 0;
      const totalProdCost = Number(row.Total_Production_Cost) || 0;
      const invoicedRev = Number(row.Invoiced_Revenue) || 0;
      const invoicedCost = Number(row.Invoiced_Cost) || 0;
      const leftToShipRev = totalProdRev - invoicedRev;
      const leftToShipCost = totalProdCost - invoicedCost;
      return {
        totalProdRev: acc.totalProdRev + totalProdRev,
        totalProdCost: acc.totalProdCost + totalProdCost,
        invoicedRev: acc.invoicedRev + invoicedRev,
        invoicedCost: acc.invoicedCost + invoicedCost,
        leftToShipRev: acc.leftToShipRev + leftToShipRev,
        leftToShipCost: acc.leftToShipCost + leftToShipCost,
        onPickRev: acc.onPickRev + (Number(row.On_Pick_Revenue) || 0),
        onPickCost: acc.onPickCost + (Number(row.On_Pick_Cost) || 0),
        receivedRev: acc.receivedRev + (Number(row.Received_Revenue) || 0),
        receivedCost: acc.receivedCost + (Number(row.Received_Cost) || 0),
      };
    }, { totalProdRev: 0, totalProdCost: 0, invoicedRev: 0, invoicedCost: 0, leftToShipRev: 0, leftToShipCost: 0, onPickRev: 0, onPickCost: 0, receivedRev: 0, receivedCost: 0 });

    const gpColorClass = (gp) => gp >= 0.3 ? 'text-green-600' : gp < 0 ? 'text-red-600' : 'text-slate-500';

    return (
      <div className={`bg-white rounded-lg border overflow-hidden shadow-sm mb-4 border-slate-200`}>
        {/* Header */}
        <div className={`px-4 py-2.5 border-b border-slate-100 bg-gradient-to-r from-${color}-50 to-white flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-md bg-${color}-100 flex items-center justify-center`}>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-800">{title}</h3>
              <p className="text-[10px] text-slate-400">
                {data.length} rows
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" style={{ maxHeight: '280px' }}>
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">{groupByField.replace('_', ' ')}</th>
                <th className="px-2 py-2 text-right font-semibold text-blue-500 border-b border-slate-200 bg-blue-50/30">Cost <span className="text-[9px] font-normal opacity-60">(Landed)</span></th>
                <th className="px-2 py-2 text-right font-semibold text-blue-600 border-b border-slate-200 bg-blue-50/50">Total Prod $</th>
                <th className="px-2 py-2 text-right font-semibold text-blue-600 border-b border-slate-200 bg-blue-50/50">GP%</th>
                <th className="px-2 py-2 text-right font-semibold text-green-600 border-b border-slate-200 bg-green-50/50">Invoice $</th>
                <th className="px-2 py-2 text-right font-semibold text-green-600 border-b border-slate-200 bg-green-50/50">GP%</th>
                <th className="px-2 py-2 text-right font-semibold text-purple-500 border-b border-slate-200 bg-purple-50/30">LTS <span className="text-[9px] font-normal opacity-60">(Landed)</span></th>
                <th className="px-2 py-2 text-right font-semibold text-purple-600 border-b border-slate-200 bg-purple-50/50">Left to Ship $</th>
                <th className="px-2 py-2 text-right font-semibold text-purple-600 border-b border-slate-200 bg-purple-50/50">GP%</th>
                <th className="px-2 py-2 text-right font-semibold text-orange-600 border-b border-slate-200 bg-orange-50/50">On Pick $</th>
                <th className="px-2 py-2 text-right font-semibold text-orange-600 border-b border-slate-200 bg-orange-50/50">GP%</th>
                <th className="px-2 py-2 text-right font-semibold text-teal-500 border-b border-slate-200 bg-teal-50/30">ATS Cost <span className="text-[9px] font-normal opacity-60">(Landed)</span></th>
                <th className="px-2 py-2 text-right font-semibold text-teal-600 border-b border-slate-200 bg-teal-50/50">ATS $</th>
                <th className="px-2 py-2 text-right font-semibold text-teal-600 border-b border-slate-200 bg-teal-50/50">GP%</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => {
                const itemValue = row[groupByField];
                const totalProdRev = Number(row.Total_Production_Revenue) || 0;
                const totalProdCost = Number(row.Total_Production_Cost) || 0;
                const invoicedRev = Number(row.Invoiced_Revenue) || 0;
                const invoicedCost = Number(row.Invoiced_Cost) || 0;
                const leftToShipRev = totalProdRev - invoicedRev;
                const leftToShipCost = totalProdCost - invoicedCost;
                const onPickRev = Number(row.On_Pick_Revenue) || 0;
                const onPickCost = Number(row.On_Pick_Cost) || 0;
                const receivedRev = Number(row.Received_Revenue) || 0;
                const receivedCost = Number(row.Received_Cost) || 0;
                const atsRev = receivedRev - invoicedRev - onPickRev;
                const atsCost = receivedCost - invoicedCost - onPickCost;

                return (
                  <tr
                    key={idx}
                    className="border-b border-slate-100 transition-colors hover:bg-slate-50"
                  >
                    <td className="px-3 py-2 font-medium truncate max-w-[160px] text-slate-700">
                      {itemValue || '-'}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-blue-500">{formatCurrency(totalProdCost)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium text-blue-700">{formatCurrency(totalProdRev)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(totalProdRev, totalProdCost))}`}>{formatPct(calcGP(totalProdRev, totalProdCost))}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-green-700">{formatCurrency(invoicedRev)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(invoicedRev, invoicedCost))}`}>{formatPct(calcGP(invoicedRev, invoicedCost))}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-purple-500">{formatCurrency(leftToShipCost)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-purple-700">{formatCurrency(leftToShipRev)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(leftToShipRev, leftToShipCost))}`}>{formatPct(calcGP(leftToShipRev, leftToShipCost))}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-orange-700">{formatCurrency(onPickRev)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(onPickRev, onPickCost))}`}>{formatPct(calcGP(onPickRev, onPickCost))}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-teal-500">{formatCurrency(atsCost)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-teal-700">{formatCurrency(atsRev)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(Math.abs(atsRev), Math.abs(atsCost)))}`}>{formatPct(calcGP(Math.abs(atsRev), Math.abs(atsCost)))}</td>
                  </tr>
                );
              })}
            </tbody>
            {data.length > 0 && (
              <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300">
                <tr className="font-semibold text-slate-700">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-blue-500">{formatCurrency(calcTotals.totalProdCost)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-blue-700">{formatCurrency(calcTotals.totalProdRev)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(calcTotals.totalProdRev, calcTotals.totalProdCost))}`}>{formatPct(calcGP(calcTotals.totalProdRev, calcTotals.totalProdCost))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-green-700">{formatCurrency(calcTotals.invoicedRev)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(calcTotals.invoicedRev, calcTotals.invoicedCost))}`}>{formatPct(calcGP(calcTotals.invoicedRev, calcTotals.invoicedCost))}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-purple-500">{formatCurrency(calcTotals.leftToShipCost)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-purple-700">{formatCurrency(calcTotals.leftToShipRev)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(calcTotals.leftToShipRev, calcTotals.leftToShipCost))}`}>{formatPct(calcGP(calcTotals.leftToShipRev, calcTotals.leftToShipCost))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-orange-700">{formatCurrency(calcTotals.onPickRev)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(calcTotals.onPickRev, calcTotals.onPickCost))}`}>{formatPct(calcGP(calcTotals.onPickRev, calcTotals.onPickCost))}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-teal-500">{formatCurrency(calcTotals.receivedCost - calcTotals.invoicedCost - calcTotals.onPickCost)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-teal-700">{formatCurrency(calcTotals.receivedRev - calcTotals.invoicedRev - calcTotals.onPickRev)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${gpColorClass(calcGP(Math.abs(calcTotals.receivedRev - calcTotals.invoicedRev - calcTotals.onPickRev), Math.abs(calcTotals.receivedCost - calcTotals.invoicedCost - calcTotals.onPickCost)))}`}>{formatPct(calcGP(Math.abs(calcTotals.receivedRev - calcTotals.invoicedRev - calcTotals.onPickRev), Math.abs(calcTotals.receivedCost - calcTotals.invoicedCost - calcTotals.onPickCost)))}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  };

  // Calculate summary totals from customerData
  const baseSummary = customerData.reduce((acc, row) => ({
    totalProdRev: acc.totalProdRev + (Number(row.Total_Production_Revenue) || 0),
    totalProdCost: acc.totalProdCost + (Number(row.Total_Production_Cost) || 0),
    receivedRev: acc.receivedRev + (Number(row.Received_Revenue) || 0),
    receivedCost: acc.receivedCost + (Number(row.Received_Cost) || 0),
    inTransitRev: acc.inTransitRev + (Number(row.In_Transit_Revenue) || 0),
    inTransitCost: acc.inTransitCost + (Number(row.In_Transit_Cost) || 0),
    wipNotTransitRev: acc.wipNotTransitRev + (Number(row.WIP_Not_In_Transit_Revenue) || 0),
    wipNotTransitCost: acc.wipNotTransitCost + (Number(row.WIP_Not_In_Transit_Cost) || 0),
    wipOtherRev: acc.wipOtherRev + (Number(row.WIP_Other_Revenue) || 0),
    wipOtherCost: acc.wipOtherCost + (Number(row.WIP_Other_Cost) || 0),
    invoicedRev: acc.invoicedRev + (Number(row.Invoiced_Revenue) || 0),
    invoicedCost: acc.invoicedCost + (Number(row.Invoiced_Cost) || 0),
    onPickRev: acc.onPickRev + (Number(row.On_Pick_Revenue) || 0),
    onPickCost: acc.onPickCost + (Number(row.On_Pick_Cost) || 0),
    openConfRev: acc.openConfRev + (Number(row.Open_Confirmed_Revenue) || 0),
    openConfCost: acc.openConfCost + (Number(row.Open_Confirmed_Cost) || 0),
    remainingRev: acc.remainingRev + (Number(row.Remaining_Open_Revenue) || 0),
    remainingCost: acc.remainingCost + (Number(row.Remaining_Open_Cost) || 0),
  }), {
    totalProdRev: 0, totalProdCost: 0, receivedRev: 0, receivedCost: 0,
    inTransitRev: 0, inTransitCost: 0, wipNotTransitRev: 0, wipNotTransitCost: 0,
    wipOtherRev: 0, wipOtherCost: 0,
    invoicedRev: 0, invoicedCost: 0, onPickRev: 0, onPickCost: 0,
    openConfRev: 0, openConfCost: 0, remainingRev: 0, remainingCost: 0
  });

  const summaryData = baseSummary;

  // Calculate GP% helper
  const gpColor = (gp) => gp >= 0.3 ? 'text-green-600' : gp < 0 ? 'text-red-600' : 'text-slate-600';
  const summary = {
    ...summaryData,
    totalProdGP: calcGP(summaryData.totalProdRev, summaryData.totalProdCost),
    receivedGP: calcGP(summaryData.receivedRev, summaryData.receivedCost),
    inTransitGP: calcGP(summaryData.inTransitRev, summaryData.inTransitCost),
    atsRev: summaryData.receivedRev - summaryData.invoicedRev - summaryData.onPickRev,
    atsCost: summaryData.receivedCost - summaryData.invoicedCost - summaryData.onPickCost,
    atsGP: calcGP(
      Math.abs(summaryData.receivedRev - summaryData.invoicedRev - summaryData.onPickRev),
      Math.abs(summaryData.receivedCost - summaryData.invoicedCost - summaryData.onPickCost)
    ),
    wipNotTransitGP: calcGP(summaryData.wipNotTransitRev, summaryData.wipNotTransitCost),
    wipOtherGP: calcGP(summaryData.wipOtherRev, summaryData.wipOtherCost),
    invoicedGP: calcGP(summaryData.invoicedRev, summaryData.invoicedCost),
    onPickGP: calcGP(summaryData.onPickRev, summaryData.onPickCost),
    openConfGP: calcGP(summaryData.openConfRev, summaryData.openConfCost),
    remainingGP: calcGP(summaryData.remainingRev, summaryData.remainingCost),
  };

  return (
    <div className="space-y-4">
      {/* Header - Sam's Club SP26 */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Sam's Club Logo */}
          <svg viewBox="0 0 100 24" className="h-5 w-auto">
            <text x="0" y="18" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="16" fill="#0066b3">Sam's Club</text>
          </svg>
          <span className="text-sm font-medium text-[#0066b3]">Sam's Club Production vs Sales</span>
          <span className="text-xs text-sky-500">| {selectedSeason} | Div 10, 60</span>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && !error && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Sam's Club SP26 Div 10, 60 Summary</h3>
          <div className="grid grid-cols-6 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-blue-600 font-medium">Total Production</p>
              <p className="text-lg font-bold text-blue-700">{formatCurrency(summary.totalProdRev)}</p>
              <p className="text-[10px] text-blue-500">Cost: {formatCurrency(summary.totalProdCost)}</p>
              <p className={`text-xs ${gpColor(summary.totalProdGP)}`}>{formatPct(summary.totalProdGP)} GP</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-green-600 font-medium">Invoice</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(summary.invoicedRev)}</p>
              <p className="text-[10px] text-green-500">Cost: {formatCurrency(summary.invoicedCost)}</p>
              <p className={`text-xs ${gpColor(summary.invoicedGP)}`}>{formatPct(summary.invoicedGP)} GP</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-purple-600 font-medium">Left to Ship</p>
              <p className="text-lg font-bold text-purple-700">{formatCurrency(summary.totalProdRev - summary.invoicedRev)}</p>
              <p className="text-[10px] text-purple-500">Cost: {formatCurrency(summary.totalProdCost - summary.invoicedCost)}</p>
              <p className={`text-xs ${gpColor(calcGP(summary.totalProdRev - summary.invoicedRev, summary.totalProdCost - summary.invoicedCost))}`}>{formatPct(calcGP(summary.totalProdRev - summary.invoicedRev, summary.totalProdCost - summary.invoicedCost))} GP</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-orange-600 font-medium">On Pick</p>
              <p className="text-lg font-bold text-orange-700">{formatCurrency(summary.onPickRev)}</p>
              <p className="text-[10px] text-orange-500">Cost: {formatCurrency(summary.onPickCost)}</p>
              <p className={`text-xs ${gpColor(summary.onPickGP)}`}>{formatPct(summary.onPickGP)} GP</p>
            </div>
            <div className="bg-teal-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-teal-600 font-medium">ATS</p>
              <p className="text-lg font-bold text-teal-700">{formatCurrency(summary.atsRev)}</p>
              <p className="text-[10px] text-teal-500">Cost: {formatCurrency(summary.atsCost)}</p>
              <p className={`text-xs ${gpColor(summary.atsGP)}`}>{formatPct(summary.atsGP)} GP</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-amber-600 font-medium">Total WIP</p>
              <p className="text-lg font-bold text-amber-700">{formatCurrency(summary.inTransitRev + summary.wipNotTransitRev)}</p>
              <p className="text-[10px] text-amber-500">Cost: {formatCurrency(summary.inTransitCost + summary.wipNotTransitCost)}</p>
              <p className="text-[9px] text-amber-500 mt-0.5">In Transit: Rev {formatCurrency(summary.inTransitRev)} | Cost {formatCurrency(summary.inTransitCost)}</p>
              <p className="text-[9px] text-amber-500">Open Prod: Rev {formatCurrency(summary.wipNotTransitRev)} | Cost {formatCurrency(summary.wipNotTransitCost)}</p>
            </div>
          </div>
        </div>
      )}

      {/* In Transit by Country of Origin & Prod Type */}
      {!loading && !error && countryOriginData.length > 0 && (() => {
        // Convert 2-letter ISO code to flag emoji
        const toFlag = (code) => {
          if (!code || code.length !== 2) return '';
          return String.fromCodePoint(
            ...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
          );
        };
        // Group by country, then by prod type within each country
        const byCountry = {};
        countryOriginData.forEach(r => {
          const country = r.CountryOrigin || 'Unknown';
          const type = r.Prod_Type || 'Direct';
          const val = Number(r.In_Transit_Cost) || 0;
          if (val <= 0) return;
          if (!byCountry[country]) byCountry[country] = { total: 0, types: {} };
          byCountry[country].total += val;
          byCountry[country].types[type] = (byCountry[country].types[type] || 0) + val;
        });
        const countries = Object.entries(byCountry)
          .sort((a, b) => b[1].total - a[1].total);
        const maxVal = countries.length > 0 ? countries[0][1].total : 1;
        const totalTransit = countries.reduce((sum, [, d]) => sum + d.total, 0);
        const typeColors = { FOB: 'bg-teal-500', DDP: 'bg-amber-500', Direct: 'bg-indigo-500' };
        if (countries.length === 0) return null;
        return (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700">In Transit by Country of Origin (Cost)</h3>
              <div className="flex items-center gap-3">
                {Object.entries(typeColors).map(([type, color]) => (
                  <span key={type} className="flex items-center gap-1">
                    <span className={`inline-block w-2.5 h-2.5 rounded-sm ${color}`} />
                    <span className="text-[10px] text-slate-500 font-medium">{type}</span>
                  </span>
                ))}
                <span className="text-[10px] text-slate-400 ml-1">|</span>
                <span className="text-xs text-teal-600 font-semibold">{formatCurrency(totalTransit)} total</span>
              </div>
            </div>
            <div className="space-y-1.5">
              {countries.map(([country, data]) => {
                const barWidth = maxVal > 0 ? Math.max((data.total / maxVal) * 100, 2) : 0;
                const pct = totalTransit > 0 ? ((data.total / totalTransit) * 100).toFixed(1) : 0;
                const flag = toFlag(country);
                return (
                  <div key={country} className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 w-24 truncate font-medium flex items-center gap-1">
                      {flag && <span className="text-sm">{flag}</span>}
                      <span>{country}</span>
                    </span>
                    <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full flex rounded-full overflow-hidden transition-all duration-500" style={{ width: `${barWidth}%` }}>
                        {['FOB', 'DDP', 'Direct'].map(type => {
                          const typeVal = Number(data.types[type]) || 0;
                          if (typeVal <= 0) return null;
                          const typePct = (typeVal / data.total) * 100;
                          return (
                            <div
                              key={type}
                              className={`h-full ${typeColors[type]} transition-all duration-500`}
                              style={{ width: `${typePct}%` }}
                              title={`${type}: ${formatCurrency(typeVal)}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                    <span className="text-xs text-slate-700 font-semibold w-20 text-right">{formatCurrency(data.total)}</span>
                    <span className="text-[10px] text-slate-400 w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Filters Card */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Exclude All Year Styles Filter */}
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={excludeAllYear}
                onChange={(e) => setExcludeAllYear(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs font-medium text-slate-600">Exclude All Year Styles</span>
            </label>
          </div>

          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button onClick={clearFilters} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">
                Reset
              </button>
            )}
            <button onClick={handleExportExcel} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
              <Download size={12} /> Excel
            </button>
            <button onClick={handleExportPDF} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-600 text-white rounded hover:bg-slate-700">
              <FileText size={12} /> PDF
            </button>
            <button onClick={fetchData} disabled={loading} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Sams Labels Filter */}
        <div className="px-4 py-2.5 bg-slate-50/50">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Labels</span>
              <span className="text-[10px] text-slate-400">(click to exclude)</span>
            </div>

            {excludedLabels.length > 0 && (
              <button
                onClick={() => setExcludedLabels([])}
                className="px-1.5 py-0.5 text-[10px] text-red-600 hover:bg-red-50 rounded flex items-center gap-0.5"
              >
                Clear excluded
              </button>
            )}

            <div className="flex flex-wrap gap-1 ml-2">
              {[...samsLabels, '(No Label)'].map(label => {
                const isExcluded = excludedLabels.includes(label);
                return (
                  <button
                    key={label}
                    onClick={() => toggleLabelExclusion(label)}
                    className={`px-2 py-0.5 text-[10px] rounded-full border transition-all ${
                      isExcluded
                        ? 'bg-red-100 text-red-600 border-red-300 line-through'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
          <RefreshCw size={20} className="animate-spin text-amber-500" />
          <span className="ml-2 text-sm text-slate-600">Loading Production vs Sales data...</span>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-lg border border-red-200">
          <AlertCircle size={20} className="text-red-500 mr-2" />
          <span className="text-sm text-red-600">{error}</span>
        </div>
      ) : (
        <>
          <DataTable
            title="By Customer"
            data={customerData}
            groupByField="Customer"
            color="purple"
          />
          <DataTable
            title="By Program"
            data={programData}
            groupByField="Program"
            color="blue"
          />
          <DataTable
            title="By Label"
            data={labelData}
            groupByField="Label"
            color="pink"
          />
        </>
      )}
    </div>
  );
}
